import { Link2, Loader2 } from "lucide-react"
import { type FormEvent, useState } from "react"
import { type PairingInvite, readPairingInvite } from "../../computers/pairing"

export function PairComputerForm({
    busy,
    onSubmit,
    compact = false,
    submitLabel = "Pair computer",
}: {
    busy: boolean
    onSubmit(input: PairingInvite): Promise<void>
    compact?: boolean
    submitLabel?: string
}) {
    const [pairingLink, setPairingLink] = useState("")
    const [error, setError] = useState<string | null>(null)

    const submit = async (event: FormEvent) => {
        event.preventDefault()
        setError(null)
        try {
            const invite = readPairingInvite(pairingLink.trim())
            if (!invite) throw new Error("Paste the full pairing link generated by WildLark")
            await onSubmit(invite)
            setPairingLink("")
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : String(cause))
        }
    }

    return (
        <form className={compact ? "grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2 max-[600px]:grid-cols-1" : "space-y-5"} onSubmit={submit}>
            <label className="block space-y-1.5">
                <span className="text-xs font-medium text-muted">Pairing link</span>
                <input
                    className="input h-10 w-full border border-border bg-input px-3 font-mono text-sm text-base-content outline-none focus:border-primary max-[600px]:h-11"
                    value={pairingLink}
                    onChange={(event) => setPairingLink(event.target.value)}
                    placeholder="https://app.wildlark.ai/pair#…"
                    inputMode="url"
                    autoCapitalize="none"
                    autoCorrect="off"
                    autoComplete="off"
                    spellCheck={false}
                    required
                />
            </label>
            <button
                type="submit"
                className={`btn flex h-10 items-center justify-center gap-2 bg-primary px-4 text-sm font-medium text-primary-content disabled:opacity-50 max-[600px]:h-11 ${
                    compact ? "min-w-40 max-[600px]:w-full" : "w-full"
                }`}
                disabled={busy}
            >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                {busy ? "Pairing…" : submitLabel}
            </button>
            {error && (
                <div role="alert" className={`border border-error/40 bg-error/10 px-3 py-2 text-sm text-error ${compact ? "col-span-full" : ""}`}>
                    {error}
                </div>
            )}
        </form>
    )
}
